import identity from 'lodash/identity';
import set from 'lodash/set';
import { createAction } from 'redux-act';
import { GET, HEAD, POST, PUT, PATCH, DELETE } from 'gandi.redux-fetcher';
import { CREATE_ONE_FAILURE, CREATE_ONE_START, CREATE_ONE_SUCCESS, DEFAULT_LABEL, DELETE_FAILURE, DELETE_ONE_FAILURE, DELETE_ONE_START, DELETE_ONE_SUCCESS, DELETE_START, DELETE_SUCCESS, FETCH_LIST_FAILURE, FETCH_LIST_START, FETCH_LIST_SUCCESS, FETCH_ONE_FAILURE, FETCH_ONE_START, FETCH_ONE_SUCCESS, HEAD_FAILURE, HEAD_START, HEAD_SUCCESS, INVALIDATE, PATCH_FAILURE, PATCH_ONE_FAILURE, PATCH_ONE_START, PATCH_ONE_SUCCESS, PATCH_START, PATCH_SUCCESS, POST_FAILURE, POST_START, POST_SUCCESS, PUT_FAILURE, PUT_START, PUT_SUCCESS, UPDATE_ONE_FAILURE, UPDATE_ONE_START, UPDATE_ONE_SUCCESS, INVALIDATE_FAILURES } from './constants';
class ExtendableError extends Error {
  constructor(message) {
    super(message);
    this.name = this.constructor.name;
    this.message = message;
    if (typeof Error.captureStackTrace === 'function') {
      Error.captureStackTrace(this, this.constructor);
    } else {
      this.stack = new Error(message).stack;
    }
  }
}
export class FetchError extends ExtendableError {
  constructor(message) {
    super(message);
    this.response = null;
  }
  static fromError(error) {
    const err = new FetchError(error.message);
    err.originalError = error;
    return err;
  }
  errors = {};
}
export function isFetchError(error) {
  return error instanceof FetchError;
}
export const parseApiErrors = response => {
  let errors = {};
  if (Array.isArray(response.data?.errors)) {
    errors = response.data.errors.reduce((acc, error) => {
      if (!error.name) {
        console.error('Unable to parse error as it does not fit the common format');
        return acc;
      }
      set(acc, error.name, error.description);
      if (error.location !== 'body' && !('_error' in acc)) {
        acc._error = error.description;
      }
      if (error.name.indexOf('.') > -1) {
        Object.defineProperty(acc, error.name.replace('.', ''), {
          get: () => {
            if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
              console.warn(`This property ${error.name.replace('.', '')} is deprecated.`, `Please use the property ${error.name.split('.').shift()} instead.`);
            }
            return error.description;
          },
          configurable: true
        });
      }
      return acc;
    }, {});
  } else if (typeof response.data?.message === 'string') {
    errors = {
      _error: response.data.message
    };
  }
  return errors;
};
export const catchApiError = message => response => {
  if (!(response && 'status' in response)) throw response;
  const error = new FetchError(message);
  error.response = response;
  if (typeof response.status === 'number' && !/^5/.test(`${response.status}`)) {
    error.errors = parseApiErrors(response);
  } else {
    error.errors = {};
  }
  throw error;
};
export const handleFailure = onFailure => err => {
  let error;
  if (isFetchError(err)) {
    error = err;
  } else {
    error = FetchError.fromError(err);
  }
  onFailure(error);
  return Promise.reject(error);
};
const computeUrl = (url, query = {}) => [url, query && Object.keys(query).filter(key => query[key] !== undefined).map(key => `${encodeURIComponent(key)}=${encodeURIComponent(query[key].toString())}`).join('&')].filter(identity).join('?');
const pathRegEx = /{([^}]*)}/g;
function splitParams(path, params) {
  const matchKeys = Array.from(path.matchAll(pathRegEx), m => m[1]);
  const pathParams = matchKeys.reduce((acc, cur) => {
    acc[cur] = params[cur];
    return acc;
  }, {});
  const queryParams = Object.keys(params).reduce((acc, cur) => {
    if (!(cur in pathParams)) {
      acc[cur] = params[cur];
    }
    return acc;
  }, {});
  return {
    pathParams,
    queryParams
  };
}
function computePath(path, params) {
  return path.replace(pathRegEx, (match, p1) => {
    if (typeof params[p1] !== 'string') {
      throw new Error(`Unable to compute path ${path}, ${p1} value must be a string.`);
    }
    if (!params[p1]) {
      throw new Error(`Unable to compute path ${path}, ${p1} value must not be empty.`);
    }
    return params[p1];
  });
}
function computeFetchArgs(path, params) {
  const {
    pathParams,
    queryParams
  } = splitParams(path, params || {});
  const builtPath = computePath(path, pathParams);
  return {
    path: builtPath,
    queryParams: params ? queryParams : undefined
  };
}
function hashParams(params) {
  const str = JSON.stringify(params);
  return String(str.split('').reduce((prevHash, curVal) => (prevHash << 5) - prevHash + curVal.charCodeAt(0), 0));
}
export function computeLabel(path, params) {
  if (!params || Object.keys(params).length === 0) {
    return 'default';
  }
  const {
    pathParams,
    queryParams
  } = splitParams(path, params);
  if (Object.keys(pathParams).length === 0) {
    return hashParams(queryParams);
  }
  return `${Object.values(pathParams).join('-')}${Object.keys(queryParams).length > 0 ? `-${hashParams(queryParams)}` : ''}`;
}
const networkHandlerTest = /^(not|on)(X|\d){3}$/;
export const ensureDefaultHandlers = (options = {}, defaultHandlers = {}) => Object.keys(options).filter(key => networkHandlerTest.test(key)).length === 0 ? {
  ...options,
  ...defaultHandlers
} : options;
const metaReducerFactory = (type, resource) => ({
  label
} = {}) => ({
  type,
  resource,
  label
});
const payloadReducer = (payload = {}) => payload;
export const createFetchAction = resource => {
  const start = createAction(`[${resource}] start fetching list of resources`, payloadReducer, metaReducerFactory(FETCH_LIST_START, resource));
  const success = createAction(`[${resource}] successfuly fetched list of resources`, payloadReducer, metaReducerFactory(FETCH_LIST_SUCCESS, resource));
  const failure = createAction(`[${resource}] failed to fetch list of resources`, payloadReducer, metaReducerFactory(FETCH_LIST_FAILURE, resource));
  function doFetchList(url, {
    query,
    label = DEFAULT_LABEL,
    params,
    ...extra
  } = {}) {
    return dispatch => {
      const payload = {
        resource,
        query,
        label,
        params,
        ...extra
      };
      dispatch(start(payload));
      const {
        path,
        queryParams
      } = computeFetchArgs(url, params);
      const definitiveUrl = computeUrl(path, query);
      return dispatch(GET(definitiveUrl, ensureDefaultHandlers(queryParams ? {
        params: queryParams,
        ...extra
      } : extra, {
        on20X: response => response,
        not20X: response => Promise.reject(response)
      }))).catch(catchApiError(`Unable to fetch collection ${resource}.`)).then(response => {
        dispatch(success({
          response,
          ...payload
        }));
        return response;
      }).catch(handleFailure(error => dispatch(failure({
        error,
        ...payload
      }))));
    };
  }
  doFetchList.start = start;
  doFetchList.success = success;
  doFetchList.failure = failure;
  return doFetchList;
};
export const createFetchOneAction = resource => {
  const start = createAction(`[${resource}] start fetching single resource`, payloadReducer, metaReducerFactory(FETCH_ONE_START, resource));
  const success = createAction(`[${resource}] successfuly fetched single resource`, payloadReducer, metaReducerFactory(FETCH_ONE_SUCCESS, resource));
  const failure = createAction(`[${resource}] failed to fetch single resource`, payloadReducer, metaReducerFactory(FETCH_ONE_FAILURE, resource));
  function doFetchOne(url, {
    query,
    label = DEFAULT_LABEL,
    params,
    ...extra
  } = {}) {
    return dispatch => {
      const payload = {
        resource,
        query,
        label,
        params,
        ...extra
      };
      dispatch(start(payload));
      const {
        path,
        queryParams
      } = computeFetchArgs(url, params);
      const definitiveUrl = computeUrl(path, query);
      return dispatch(GET(definitiveUrl, ensureDefaultHandlers(queryParams ? {
        params: queryParams,
        ...extra
      } : extra, {
        on20X: response => response,
        not20X: response => Promise.reject(response)
      }))).catch(catchApiError(`Unable to fetch resource ${resource}.`)).then(response => {
        dispatch(success({
          response,
          ...payload
        }));
        return response;
      }).catch(handleFailure(error => dispatch(failure({
        error,
        ...payload
      }))));
    };
  }
  doFetchOne.start = start;
  doFetchOne.success = success;
  doFetchOne.failure = failure;
  return doFetchOne;
};
export const createCreateOneAction = resource => {
  const start = createAction(`[${resource}] start creating single resource`, payloadReducer, metaReducerFactory(CREATE_ONE_START, resource));
  const success = createAction(`[${resource}] successfuly created single resource`, payloadReducer, metaReducerFactory(CREATE_ONE_SUCCESS, resource));
  const failure = createAction(`[${resource}] failed to create single resource`, payloadReducer, metaReducerFactory(CREATE_ONE_FAILURE, resource));
  function doCreateOne(url, doc, {
    query,
    label = DEFAULT_LABEL,
    params,
    ...extra
  } = {}) {
    return dispatch => {
      const payload = {
        resource,
        query,
        label,
        params,
        ...extra
      };
      dispatch(start(payload));
      const {
        path,
        queryParams
      } = computeFetchArgs(url, params);
      const definitiveUrl = computeUrl(path, query);
      return dispatch(POST(definitiveUrl, doc, ensureDefaultHandlers(queryParams ? {
        params: queryParams,
        ...extra
      } : extra, {
        on20X: response => response,
        not20X: response => Promise.reject(response)
      }))).catch(catchApiError(`Unable to create resource ${resource}.`)).then(response => {
        dispatch(success({
          response,
          ...payload
        }));
        return response;
      }).catch(handleFailure(error => dispatch(failure({
        error,
        ...payload
      }))));
    };
  }
  doCreateOne.start = start;
  doCreateOne.success = success;
  doCreateOne.failure = failure;
  return doCreateOne;
};
export const createUpdateOneAction = resource => {
  const start = createAction(`[${resource}] start updating single resource`, payloadReducer, metaReducerFactory(UPDATE_ONE_START, resource));
  const success = createAction(`[${resource}] successfuly updated single resource`, payloadReducer, metaReducerFactory(UPDATE_ONE_SUCCESS, resource));
  const failure = createAction(`[${resource}] failed to update single resource`, payloadReducer, metaReducerFactory(UPDATE_ONE_FAILURE, resource));
  function doUpdateOne(url, doc, {
    query,
    label = DEFAULT_LABEL,
    params,
    ...extra
  } = {}) {
    return dispatch => {
      const payload = {
        resource,
        query,
        label,
        params,
        ...extra
      };
      dispatch(start(payload));
      const {
        path,
        queryParams
      } = computeFetchArgs(url, params);
      const definitiveUrl = computeUrl(path, query);
      return dispatch(PUT(definitiveUrl, doc, ensureDefaultHandlers(queryParams ? {
        params: queryParams,
        ...extra
      } : extra, {
        on20X: response => response,
        not20X: response => Promise.reject(response)
      }))).catch(catchApiError(`Unable to update resource ${resource}.`)).then(response => {
        dispatch(success({
          response,
          ...payload
        }));
        return response;
      }).catch(handleFailure(error => dispatch(failure({
        error,
        ...payload
      }))));
    };
  }
  doUpdateOne.start = start;
  doUpdateOne.success = success;
  doUpdateOne.failure = failure;
  return doUpdateOne;
};
export const createPostAction = resource => {
  const start = createAction(`[${resource}] start POST request`, payloadReducer, metaReducerFactory(POST_START, resource));
  const success = createAction(`[${resource}] successfull POST request`, payloadReducer, metaReducerFactory(POST_SUCCESS, resource));
  const failure = createAction(`[${resource}] failed POST request`, payloadReducer, metaReducerFactory(POST_FAILURE, resource));
  function doPost(url, body, {
    query,
    label = DEFAULT_LABEL,
    params,
    ...extra
  } = {}) {
    return dispatch => {
      const payload = {
        resource,
        query,
        label,
        params,
        ...extra
      };
      dispatch(start(payload));
      const {
        path,
        queryParams
      } = computeFetchArgs(url, params);
      const definitiveUrl = computeUrl(path, query);
      return dispatch(POST(definitiveUrl, body, ensureDefaultHandlers(queryParams ? {
        params: queryParams,
        ...extra
      } : extra, {
        on20X: response => response,
        not20X: response => Promise.reject(response)
      }))).catch(catchApiError(`Failed POST request for ${resource}.`)).then(response => {
        dispatch(success({
          response,
          ...payload
        }));
        return response;
      }).catch(handleFailure(error => dispatch(failure({
        error,
        ...payload
      }))));
    };
  }
  doPost.start = start;
  doPost.success = success;
  doPost.failure = failure;
  return doPost;
};
export const createPutAction = resource => {
  const start = createAction(`[${resource}] start PUT request`, payloadReducer, metaReducerFactory(PUT_START, resource));
  const success = createAction(`[${resource}] successfull PUT request`, payloadReducer, metaReducerFactory(PUT_SUCCESS, resource));
  const failure = createAction(`[${resource}] failed PUT request`, payloadReducer, metaReducerFactory(PUT_FAILURE, resource));
  function doPut(url, body, {
    query,
    label = DEFAULT_LABEL,
    params,
    ...extra
  } = {}) {
    return dispatch => {
      const payload = {
        resource,
        query,
        label,
        params,
        ...extra
      };
      dispatch(start(payload));
      const {
        path,
        queryParams
      } = computeFetchArgs(url, params);
      const definitiveUrl = computeUrl(path, query);
      return dispatch(PUT(definitiveUrl, body, ensureDefaultHandlers(queryParams ? {
        params: queryParams,
        ...extra
      } : extra, {
        on20X: response => response,
        not20X: response => Promise.reject(response)
      }))).catch(catchApiError(`Failed PUT request for ${resource}.`)).then(response => {
        dispatch(success({
          response,
          ...payload
        }));
        return response;
      }).catch(handleFailure(error => dispatch(failure({
        error,
        ...payload
      }))));
    };
  }
  doPut.start = start;
  doPut.success = success;
  doPut.failure = failure;
  return doPut;
};
export const createPatchAction = resource => {
  const start = createAction(`[${resource}] start PATCH request`, payloadReducer, metaReducerFactory(PATCH_START, resource));
  const success = createAction(`[${resource}] successfull PATCH request`, payloadReducer, metaReducerFactory(PATCH_SUCCESS, resource));
  const failure = createAction(`[${resource}] failed PATCH request`, payloadReducer, metaReducerFactory(PATCH_FAILURE, resource));
  function doPatch(url, body, {
    query,
    label = DEFAULT_LABEL,
    params,
    ...extra
  } = {}) {
    return dispatch => {
      const payload = {
        resource,
        query,
        label,
        params,
        ...extra
      };
      dispatch(start(payload));
      const {
        path,
        queryParams
      } = computeFetchArgs(url, params);
      const definitiveUrl = computeUrl(path, query);
      return dispatch(PATCH(definitiveUrl, body, ensureDefaultHandlers(queryParams ? {
        params: queryParams,
        ...extra
      } : extra, {
        on20X: response => response,
        not20X: response => Promise.reject(response)
      }))).catch(catchApiError(`Failed PATCH request for ${resource}.`)).then(response => {
        dispatch(success({
          response,
          ...payload
        }));
        return response;
      }).catch(handleFailure(error => dispatch(failure({
        error,
        ...payload
      }))));
    };
  }
  doPatch.start = start;
  doPatch.success = success;
  doPatch.failure = failure;
  return doPatch;
};
export const createHeadAction = resource => {
  const start = createAction(`[${resource}] start HEAD request`, payloadReducer, metaReducerFactory(HEAD_START, resource));
  const success = createAction(`[${resource}] successfull HEAD request`, payloadReducer, metaReducerFactory(HEAD_SUCCESS, resource));
  const failure = createAction(`[${resource}] failed HEAD request`, payloadReducer, metaReducerFactory(HEAD_FAILURE, resource));
  function doHead(url, {
    query,
    label = DEFAULT_LABEL,
    params,
    ...extra
  } = {}) {
    return dispatch => {
      const payload = {
        resource,
        query,
        label,
        params,
        ...extra
      };
      dispatch(start(payload));
      const {
        path,
        queryParams
      } = computeFetchArgs(url, params);
      const definitiveUrl = computeUrl(path, query);
      return dispatch(HEAD(definitiveUrl, ensureDefaultHandlers(queryParams ? {
        params: queryParams,
        ...extra
      } : extra, {
        on20X: response => response,
        not20X: response => Promise.reject(response)
      }))).catch(catchApiError(`Failed HEAD request for ${resource}.`)).then(response => {
        dispatch(success({
          response,
          ...payload
        }));
        return response;
      }).catch(handleFailure(error => dispatch(failure({
        error,
        ...payload
      }))));
    };
  }
  doHead.start = start;
  doHead.success = success;
  doHead.failure = failure;
  return doHead;
};
export const createPatchOneAction = resource => {
  const start = createAction(`[${resource}] start patch single resource`, payloadReducer, metaReducerFactory(PATCH_ONE_START, resource));
  const success = createAction(`[${resource}] successfuly updated single resource`, payloadReducer, metaReducerFactory(PATCH_ONE_SUCCESS, resource));
  const failure = createAction(`[${resource}] failed to update single resource`, payloadReducer, metaReducerFactory(PATCH_ONE_FAILURE, resource));
  function doPatchOne(url, doc, {
    query,
    label = DEFAULT_LABEL,
    params,
    ...extra
  } = {}) {
    return dispatch => {
      const payload = {
        resource,
        query,
        label,
        params,
        ...extra
      };
      dispatch(start(payload));
      const {
        path,
        queryParams
      } = computeFetchArgs(url, params);
      const definitiveUrl = computeUrl(path, query);
      return dispatch(PATCH(definitiveUrl, doc, ensureDefaultHandlers(queryParams ? {
        params: queryParams,
        ...extra
      } : extra, {
        on20X: response => response,
        not20X: response => Promise.reject(response)
      }))).catch(catchApiError(`Unable to patch resource ${resource}.`)).then(response => {
        dispatch(success({
          response,
          ...payload
        }));
        return response;
      }).catch(handleFailure(error => dispatch(failure({
        error,
        ...payload
      }))));
    };
  }
  doPatchOne.start = start;
  doPatchOne.success = success;
  doPatchOne.failure = failure;
  return doPatchOne;
};
export const createDeleteOneAction = resource => {
  const start = createAction(`[${resource}] start deleting single resource`, payloadReducer, metaReducerFactory(DELETE_ONE_START, resource));
  const success = createAction(`[${resource}] successfuly deleted single resource`, payloadReducer, metaReducerFactory(DELETE_ONE_SUCCESS, resource));
  const failure = createAction(`[${resource}] failed to delete single resource`, payloadReducer, metaReducerFactory(DELETE_ONE_FAILURE, resource));
  function doDeleteOne(url, ...args) {
    return dispatch => {
      const legacy = args.length === 1;
      const options = args[args.length - 1];
      const {
        query,
        label = DEFAULT_LABEL,
        params,
        ...extra
      } = options || {};
      const payload = {
        resource,
        query,
        label,
        params,
        ...extra
      };
      dispatch(start(payload));
      const {
        path,
        queryParams
      } = computeFetchArgs(url, params);
      const definitiveUrl = computeUrl(path, query);
      const doc = args[0];
      const finalOptions = ensureDefaultHandlers(queryParams ? {
        params: queryParams,
        ...extra
      } : extra, {
        on20X: response => response,
        not20X: response => Promise.reject(response)
      });
      const deleteArgs = legacy ? [undefined, finalOptions] : [doc, finalOptions];
      return dispatch(DELETE(definitiveUrl, ...deleteArgs)).catch(catchApiError(`Unable to delete resource ${resource}.`)).then(response => {
        dispatch(success({
          response,
          ...payload
        }));
        return response;
      }).catch(handleFailure(error => dispatch(failure({
        error,
        ...payload
      }))));
    };
  }
  doDeleteOne.start = start;
  doDeleteOne.success = success;
  doDeleteOne.failure = failure;
  return doDeleteOne;
};
export const createDeleteAction = resource => {
  const start = createAction(`[${resource}] start DELETE request`, payloadReducer, metaReducerFactory(DELETE_START, resource));
  const success = createAction(`[${resource}] successfuly DELETE request`, payloadReducer, metaReducerFactory(DELETE_SUCCESS, resource));
  const failure = createAction(`[${resource}] failed DELETE request`, payloadReducer, metaReducerFactory(DELETE_FAILURE, resource));
  function doDelete(url, doc, {
    query,
    label = DEFAULT_LABEL,
    params,
    ...extra
  } = {}) {
    return dispatch => {
      const payload = {
        resource,
        query,
        label,
        params,
        ...extra
      };
      dispatch(start(payload));
      const {
        path,
        queryParams
      } = computeFetchArgs(url, params);
      const definitiveUrl = computeUrl(path, query);
      const finalOptions = ensureDefaultHandlers(queryParams ? {
        params: queryParams,
        ...extra
      } : extra, {
        on20X: response => response,
        not20X: response => Promise.reject(response)
      });
      const deleteArgs = [doc, finalOptions];
      return dispatch(DELETE(definitiveUrl, ...deleteArgs)).catch(catchApiError(`Failed DELETE request for ${resource}.`)).then(response => {
        dispatch(success({
          response,
          ...payload
        }));
        return response;
      }).catch(handleFailure(error => dispatch(failure({
        error,
        ...payload
      }))));
    };
  }
  doDelete.start = start;
  doDelete.success = success;
  doDelete.failure = failure;
  return doDelete;
};
export const createInvalidateAction = resource => createAction(`[${resource}] invalidate whole resource`, payloadReducer, metaReducerFactory(INVALIDATE, resource));
export const invalidateFailures = createAction(`[resources] invalidate resources in a failure state`, undefined, () => ({
  type: INVALIDATE_FAILURES
}));